import * as React from "react";
import { lazy, Suspense } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import ProtectedRoute from "../../components/protectedRoute";
// layouts
import MainLayout from "../../layouts/dashboard";
import MSLayout from "../../layouts/ms-layout";
// ----------------------------------------------------------------------
import { HatLoading } from "../../components/loading-screen";
import { BannerDisplay } from "../../components/BannerDisplay";
import { PosthogFeatureFlag, FeatureFlag } from "../../components/FeatureFlag";
import { PaletteDebug } from "../../pages/debug/palette-debug";
import { HlDebug } from "../../pages/debug/hldebug";
import { FeatureFlags } from "../../pages/debug/feature-flags";
import { AwardsDebug } from "../../pages/debug/AwardsDebug";
import { FacetsDebug } from "../../pages/debug/FacetsDebug";
import { ResetUserPage } from "../../pages/debug/ResetUserPage";
import { NextStepsDebug } from "../../pages/debug/NextStepsDebug";
import { HatsDebug } from "../../pages/debug/Hats-Debug";
import { PDFPreviewPage } from "../../pages/debug/pdf-preview";
import { CoursePlanPDFPreviewPage } from "../../pages/debug/course-plan-pdf-preview";
import { CertificatesDebug } from "../../pages/debug/CertificatesDebug";
import { IbmSync } from "../../pages/ibm-sync/IbmSync";

const RedirectToClimberDetail = () => {
  const { climberId } = useParams();
  return <Navigate to={`/users/climber/${climberId}`} replace />;
};

const Page404 = lazy(() => import("@/pages/error-pages/not-found-page"));
const BrowseLadders = lazy(() => import("../../pages/ladders"));
const Login = lazy(() => import("../../pages/login"));
const PostLogin = lazy(() => import("../../pages/post-login"));

const ClimberDashboard = lazy(() => import("../../pages/climber-dashboard"));
const MSClimberDashboard = lazy(
  () => import("../../pages/middle-school/ms-climber-dashboard"),
);
const MSClimberDashboardCheckin = lazy(
  () =>
    import("../../pages/middle-school/ms-climber-dashboard-checkin/index.tsx"),
);
const MSClimberProfile = lazy(
  () => import("../../pages/middle-school/ms-climber-profile"),
);
const ClustersAndPathways = lazy(
  () => import("../../pages/middle-school/ms-clusters-and-pathways"),
);
const MSCareerCoursePlanner = lazy(
  () => import("../../pages/middle-school/ms-career-course-planner/index.tsx"),
);
const MSCareerCoursePlannerComingSoon = lazy(
  () =>
    import("../../pages/middle-school/ms-career-course-planner/old-index.jsx"),
);
const MSCoursePlanner = lazy(
  () => import("../../pages/middle-school/ms-course-planner/index.tsx"),
);
const MSCoursePlanReview = lazy(
  () => import("../../pages/middle-school/ms-course-plan-review/index.tsx"),
);
const MSSchoolPlans = lazy(
  () => import("../../pages/middle-school/ms-school-plans/index.tsx"),
);
const TeacherMSClimberExperienceSetup = lazy(
  () =>
    import(
      "../../pages/middle-school/ms-teacher-climber-experience-setup/index.tsx"
    ),
);
const MSHatV2Page = lazy(
  () => import("../../pages/middle-school/ms-hats-v2/MSHatV2Page"),
);
const Cluster = lazy(() => import("../../pages/middle-school/ms-cluster"));
const Pathway = lazy(() => import("../../pages/middle-school/ms-pathway"));
const TeacherDashboard = lazy(() => import("../../pages/teacher-dashboard"));
const WorksitesPage = lazy(() => import("../../pages/worksites"));
const SuperAdminDashboard = lazy(
  () => import("../../pages/super-admin-dashboard"),
);
const DistrictAdminDashboard = lazy(
  () => import("../../pages/district-admin-dashboard"),
);

const SchoolStaffDashboard = lazy(
  () => import("../../pages/school-staff-dashboard"),
);

const Achievements = lazy(() => import("../../pages/achievements"));

const Sites = lazy(() => import("../../pages/sites"));
const SchoolGroups = lazy(() => import("../../pages/school-groups"));

const ManageCoaches = lazy(() => import("../../pages/manage-coaches"));
const ManageStaff = lazy(() => import("../../pages/manage-staff"));
const ManageAdmins = lazy(() => import("../../pages/manage-admins"));
const ClimberSearch = lazy(() => import("../../pages/climber-search"));
const ClimberDetail = lazy(() => import("../../pages/climber-detail"));
const Settings = lazy(() => import("../../pages/district-settings"));
const CoursePlanConfig = lazy(() => import("../../pages/course-plan-config"));

const Courses = lazy(() => import("../../pages/courses"));
const Portfolio = lazy(() => import("../../pages/portfolio"));
const HatsMain = lazy(() => import("../../pages/hats"));
const MSHats = lazy(() => import("../../pages/middle-school/ms-hats"));
const GroupReport = lazy(() => import("../../pages/reports"));
const CareerClimbs = lazy(
  () => import("../../pages/career-climbs/climberCareerClimbPage"),
);
const ClimberList = lazy(() => import("../../pages/reports/climber-list"));
const ClimberCareerClimbItem = lazy(
  () =>
    import("../../pages/career-climbs/components/climber-career-climb-item"),
);
const CoachCareerClimbs = lazy(
  () => import("../../pages/career-climbs/coachCareerClimbPage"),
);
const CoachCareerClimbItem = lazy(
  () => import("../../pages/career-climbs/components/coach-career-climb-item"),
);
const Onboarding = lazy(() => import("../../pages/onboarding"));

const QucksightReport = lazy(
  () => import("../../pages/reports/quicksight-reports"),
);
const EmbeddableReport = lazy(
  () => import("../../pages/reports/embeddable-reports"),
);

const ReportsDashboard = lazy(
  () => import("../../pages/reports/report-dashboard"),
);
const PlanInsightsPage = lazy(
  () => import("../../pages/reports/plan-insights"),
);

const LadderIframe = lazy(() => import("../../pages/ladders/ladderIframe"));
const Wizard = lazy(() => import("../../pages/middle-school/ms-wizard"));
const MSProfileOnboard = lazy(
  () => import("../../pages/middle-school/ms-profile-onboard"),
);
const CareerClimbReport = lazy(
  () => import("../../pages/reports/career-climb-report"),
);
const CoachGroups = lazy(() => import("../../pages/coach-groups"));
const CoachGroup = lazy(() => import("../../pages/reports"));
const LadderActivity = lazy(() => import("../../pages/ladders/ladderActivity"));
const ChangelogPage = lazy(
  () => import("../../pages/changelog/changelog-page"),
);
const PathwayContentPage = lazy(
  () => import("../../pages/middle-school/ms-pathway/PathwayContentPage"),
);
const EducatorDownloads = lazy(() => import("../../pages/educator-downloads"));
const StudentAnswerReview = lazy(
  () => import("../../pages/student-answer-review"),
);
const Planner = lazy(() => import("../../pages/planner"));
const StaffCareerPlanPage = lazy(() => import("../../pages/staff-career-plan"));
const PlanAdmin = lazy(() => import("../../pages/plan-admin"));
const WorksiteVisitProgressPage = lazy(
  () => import("../../pages/reports/worksite-visit-progress/index.tsx"),
);
const WorksiteVisitProgressTeacherDetailPage = lazy(
  () =>
    import(
      "../../pages/reports/worksite-visit-progress/teacher-detail/index.tsx"
    ),
);
const WorksiteVisitProgressSchoolDetailPage = lazy(
  () =>
    import(
      "../../pages/reports/worksite-visit-progress/school-detail/index.tsx"
    ),
);
const PathwayInterestReport = lazy(
  () => import("../../pages/reports/pathway-interest-report/index.tsx"),
);
const CertificateReport = lazy(() =>
  import("../../pages/reports/certificate-report/CertificateReport").then(
    (m) => ({ default: m.CertificateReport }),
  ),
);
const CertificateClassView = lazy(() =>
  import("../../pages/reports/certificate-report/ClassView").then((m) => ({
    default: m.ClassView,
  })),
);
const ClusterProgressReport = lazy(
  () => import("../../pages/reports/cluster-progress-report/index.tsx"),
);

// ----------------------------------------------------------------------

export const mainRoutes = [
  {
    // -------- onboarding for Climbers ----------
    element: (
      <ProtectedRoute requiredRole={["climber"]}>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "onboarding",
        element: <Outlet />,
        children: [{ element: <Onboarding />, index: true }],
      },
    ],
  },
  {
    element: (
      <ProtectedRoute requiredLevel={["hs", "ms", "wf"]}>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "ladder-iframe/:id",
        element: <Outlet />,
        children: [{ element: <LadderIframe />, index: true }],
      },
    ],
  },

  // -------- Ladders & Achievements for Climbers ----------
  {
    element: (
      <ProtectedRoute requiredLevel={["hs", "wf"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "ladders",
        element: <Outlet />,
        children: [{ element: <BrowseLadders />, index: true }],
      },
      {
        path: "achievements",
        element: <Outlet />,
        children: [{ element: <Achievements />, index: true }],
      },
    ],
  },
  // -------- Full screen content for Climbers ----------
  {
    element: (
      <Suspense fallback={<HatLoading />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: "ladders/:id",
        element: <Outlet />,
        children: [{ element: <LadderActivity />, index: true }],
      },
    ],
  },
  // -------- Full screen content for Staff (Teachers, School Staff, District Admins) ----------
  {
    element: (
      <ProtectedRoute
        requiredRole={["teacher", "schoolstaff", "districtadmin", "superadmin"]}
      >
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "climber-experience/ladders/:id",
        element: <Outlet />,
        children: [{ element: <LadderActivity />, index: true }],
      },
    ],
  },
  {
    // --------- Courses for Climbers ----------
    element: (
      <ProtectedRoute requiredLevel={["hs", "wf"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "courses",
        element: <Outlet />,
        children: [
          { element: <Courses />, index: true },
          {
            path: "/courses/career-climbs/:id",
            element: <ClimberCareerClimbItem />,
          },
        ],
      },
    ],
  },
  {
    // -------- Hats for climber ----------
    element: (
      <ProtectedRoute requiredLevel={["hs", "wf"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "hats/*",
        element: <HatsMain />,
      },
    ],
  },
  {
    // -------- CareerClimbs for climber ----------
    element: (
      <ProtectedRoute requiredLevel={["hs", "wf"]}>
        <MainLayout sx={{ p: "0 !important" }}>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "career-climbs",
        element: <Outlet />,
        children: [
          { element: <CareerClimbs />, index: true },
          {
            path: "/career-climbs/:id",
            element: <ClimberCareerClimbItem />,
          },
        ],
      },
    ],
  },
  {
    // -------- Climber Dashboard ----------
    element: (
      <MainLayout>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </MainLayout>
    ),
    children: [
      {
        path: "/climber/dashboard",
        children: [
          {
            element: (
              <ProtectedRoute
                requiredRole={["climber"]}
                requiredLevel={["hs", "wf"]}
              >
                <ClimberDashboard />
              </ProtectedRoute>
            ),
            index: true,
          },
        ],
      },
    ],
  },
  // -------- MS Climbers ----------

  {
    element: (
      <ProtectedRoute
        requiredRole={["climber", "teacher", "schoolstaff", "districtadmin"]}
        requiredLevel={["ms"]}
      >
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "ms/climber/climb/:id",
        element: (
          <MSLayout backLabel="Back">
            <ClimberCareerClimbItem breadcrumb={false} middleSchool={true} />
          </MSLayout>
        ),
      },
      // Planner
      {
        path: "ms/planner/*",
        element: (
          <PosthogFeatureFlag flag="ms-career-plan">
            <Planner />
          </PosthogFeatureFlag>
        ),
      },

      {
        path: "ms/climber/cluster/:clusterId",
        element: <Cluster />,
      },
      {
        path: "ms/climber/pathway/:pathwayId",
        element: <Pathway />,
      },

      {
        path: "ms/climber/dashboard",
        element: (
          <>
            <BannerDisplay />
            <MSClimberDashboard />
          </>
        ),
      },
      {
        path: "ms/climber/dashboard/checkin",
        element: <MSClimberDashboardCheckin />,
      },
      {
        path: "ms/climber/profile",
        element: <MSClimberProfile />,
      },
      {
        path: "ms/climber/cluster-pathway",
        element: <ClustersAndPathways />,
      },
      {
        path: "ms/climber/pathway/:pathwayId/content/:contentId",
        element: <PathwayContentPage />,
      },
      {
        path: "ms/climber/wizard",
        element: <Wizard />,
      },
      {
        path: "ms/climber/onboard/profile",
        element: <MSProfileOnboard />,
      },
      {
        path: "ms/hats/*",
        element: <MSHats />,
      },
      {
        path: "ms/hats_v2/:hatId",
        element: <MSHatV2Page />,
      },
    ],
  },

  {
    element: (
      <ProtectedRoute
        requiredRole={["schoolstaff", "districtadmin"]}
        requiredLevel={["ms"]}
      >
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "staff/career-course-planner/:teacherId",
        element: (
          <PosthogFeatureFlag flag="ms-career-plan-coach">
            <MSCareerCoursePlanner />
          </PosthogFeatureFlag>
        ),
      },
    ],
  },
  // -------- MS Coaches ---------

  {
    element: (
      <ProtectedRoute
        requiredRole={["teacher", "schoolstaff", "districtadmin", "superadmin"]}
        requiredLevel={["ms"]}
      >
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "/teacher/ms-climber-experience",
        element: <TeacherMSClimberExperienceSetup />,
      },
      {
        path: "staff/career-course-planner",
        element: (
          <PosthogFeatureFlag flag="ms-career-plan-coach">
            <MSCareerCoursePlanner />
          </PosthogFeatureFlag>
        ),
      },
      {
        path: "staff/school-plans",
        element: (
          <PosthogFeatureFlag flag="ms-career-plan-coach">
            <MSSchoolPlans />
          </PosthogFeatureFlag>
        ),
      },
      {
        path: "staff/career-course-planner-coming-soon",
        element: <MSCareerCoursePlannerComingSoon />,
      },
    ],
  },

  // -------- MS School Staff section removed - they use unified non-MS routes

  {
    // -------- class section ----------
    element: (
      <MainLayout>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </MainLayout>
    ),
    children: [
      {
        path: "/section/:id",
        children: [
          {
            element: (
              <ProtectedRoute
                requiredRole={["schoolstaff", "districtadmin"]}
                requiredLevel={["ms", "hs"]}
              >
                <GroupReport />
              </ProtectedRoute>
            ),
            index: true,
          },
        ],
      },
    ],
  },

  // -------- MS Admins (reuse unified dashboard) ----------
  {
    element: (
      <ProtectedRoute requiredRole={["districtadmin"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      // ms/districtadmin/dashboard removed in favor of unified district admin dashboard
      {
        path: "settings",
        children: [
          {
            element: (
              <ProtectedRoute requiredRole={["districtadmin"]}>
                <Settings />
              </ProtectedRoute>
            ),
            index: true,
          },
        ],
      },
      {
        path: "settings/courses",
        element: (
          <ProtectedRoute requiredRole={["districtadmin"]}>
            <CoursePlanConfig />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    // -------- Ladders for Climbers ----------
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "ladders",
        element: <Outlet />,
        children: [{ element: <BrowseLadders />, index: true }],
      },
      {
        path: "achievements",
        element: <Outlet />,
        children: [{ element: <Achievements />, index: true }],
      },
    ],
  },
  {
    element: (
      <Suspense fallback={<HatLoading />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: "riasec",
        element: <Navigate to="/hldebug/riasec" />,
      },
      {
        path: "palette",
        element: <Navigate to="/hldebug/palette" />,
      },
      {
        path: "hldebug",
        element: <Outlet />,
        children: [
          { element: <HlDebug />, index: true },
          { path: "facets", element: <FacetsDebug /> },
          { path: "awards", element: <AwardsDebug /> },
          { path: "next-steps", element: <NextStepsDebug /> },
          { path: "ms-hats-country", element: <HatsDebug /> },
          { path: "palette", element: <PaletteDebug /> },
          { path: "flags", element: <FeatureFlags /> },
          { path: "reset-user", element: <ResetUserPage /> },
          { path: "pdf-preview", element: <PDFPreviewPage /> },
          {
            path: "course-plan-pdf-preview",
            element: <CoursePlanPDFPreviewPage />,
          },
          { path: "certificates", element: <CertificatesDebug /> },
          { path: "rustici", element: <Navigate to="/ibm-sync" /> },
        ],
      },
    ],
  },
  {
    // -------- Full screen content for Climbers ----------
    element: (
      <ProtectedRoute>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "ladders/:id",
        element: <Outlet />,
        children: [{ element: <LadderActivity />, index: true }],
      },
      { path: "ibm-sync", element: <IbmSync /> },
    ],
  },

  // -------- Climber Routes (HS/WF) ----------
  {
    element: (
      <ProtectedRoute requiredLevel={["hs", "wf"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      // Courses
      {
        path: "courses",
        element: <Outlet />,
        children: [
          { element: <Courses />, index: true },
          { path: "career-climbs/:id", element: <ClimberCareerClimbItem /> },
        ],
      },
      // Hats
      { path: "hats/*", element: <HatsMain /> },
      // Portfolio
      { path: "portfolio", element: <Portfolio /> },
      // Changelog
      { path: "changes", element: <ChangelogPage /> },
    ],
  },

  // -------- Career Climbs for Climbers (special layout) ----------
  {
    element: (
      <ProtectedRoute requiredLevel={["hs", "wf"]}>
        <MainLayout sx={{ p: "0 !important" }}>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "career-climbs",
        element: <Outlet />,
        children: [
          { element: <CareerClimbs />, index: true },
          { path: ":id", element: <ClimberCareerClimbItem /> },
        ],
      },
    ],
  },
  {
    element: (
      <ProtectedRoute requiredRole={["schoolstaff", "districtadmin"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      // Reports
      {
        path: "reports",
        element: <Outlet />,
        children: [{ path: "plan-admin", element: <PlanAdmin /> }], // This is not available to teachers.
      },
    ],
  },

  {
    // -------- Staff Routes ----------
    element: (
      <ProtectedRoute
        requiredRole={["teacher", "schoolstaff", "districtadmin", "superadmin"]}
      >
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      // Reports
      {
        path: "reports",
        element: <Outlet />,
        children: [
          { path: "dashboard", element: <ReportsDashboard />, index: true },
          { path: "qs/:reportId", element: <QucksightReport /> },
          { path: "embeddable/:reportId", element: <EmbeddableReport /> },
          { path: "plan-insights", element: <PlanInsightsPage /> },
          { path: "plan-admin", element: <PlanAdmin /> },
          { path: "pathway-interests", element: <PathwayInterestReport /> },
          {
            path: "cluster-progress-report",
            element: <ClusterProgressReport />,
          },
          {
            path: "worksite-visit-progress",
            element: (
              <ProtectedRoute requiredRole={["districtadmin"]}>
                <FeatureFlag flags={["wbl-staff"]}>
                  <PosthogFeatureFlag flag="worksite-visit-report">
                    <WorksiteVisitProgressPage />
                  </PosthogFeatureFlag>
                </FeatureFlag>
              </ProtectedRoute>
            ),
          },
          {
            path: "worksite-visit-progress/school/:schoolId",
            element: (
              <ProtectedRoute requiredRole={["districtadmin"]}>
                <FeatureFlag flags={["wbl-staff"]}>
                  <PosthogFeatureFlag flag="worksite-visit-report">
                    <WorksiteVisitProgressSchoolDetailPage />
                  </PosthogFeatureFlag>
                </FeatureFlag>
              </ProtectedRoute>
            ),
          },
          {
            path: "worksite-visit-progress/:teacherId",
            element: (
              <ProtectedRoute requiredRole={["districtadmin"]}>
                <FeatureFlag flags={["wbl-staff"]}>
                  <PosthogFeatureFlag flag="worksite-visit-report">
                    <WorksiteVisitProgressTeacherDetailPage />
                  </PosthogFeatureFlag>
                </FeatureFlag>
              </ProtectedRoute>
            ),
          },
          {
            path: "certificate-completion",
            element: (
              <ProtectedRoute
                requiredRole={[
                  "teacher",
                  "schoolstaff",
                  "districtadmin",
                  "superadmin",
                ]}
              >
                <CertificateReport />
              </ProtectedRoute>
            ),
          },
          {
            path: "certificate-completion/:sectionId",
            element: (
              <ProtectedRoute
                requiredRole={[
                  "teacher",
                  "schoolstaff",
                  "districtadmin",
                  "superadmin",
                ]}
              >
                <CertificateClassView />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Groups
      {
        path: "groups",
        element: <Outlet />,
        children: [
          { element: <CoachGroups />, index: true },
          { path: ":id", element: <CoachGroup /> },
          {
            path: ":id/climber/:climberId",
            element: <RedirectToClimberDetail />,
          },
          { path: ":id/career-climb/:climbID", element: <CareerClimbReport /> },
        ],
      },
      // Worksites (Teachers only, WF/WBL only, or districtadmins viewing teacher data)
      {
        path: "staff/worksites",
        element: (
          <ProtectedRoute
            requiredRole={["teacher", "districtadmin"]}
            requiredLevel={["hs"]}
          >
            <WorksitesPage />
          </ProtectedRoute>
        ),
      },
      // Career Plan
      {
        path: "teacher/career-plan/:studentId/:careerPlanId",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <StaffCareerPlanPage />
          </ProtectedRoute>
        ),
      },
      // Course Plan
      {
        path: "teacher/course-plan/:climberId/:coursePlanId",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <PosthogFeatureFlag flag="ms-course-plan">
              <MSCoursePlanReview />
            </PosthogFeatureFlag>
          </ProtectedRoute>
        ),
      },
      // User Management
      {
        path: "users/coaches",
        element: (
          <ProtectedRoute requiredRole={["schoolstaff", "districtadmin"]}>
            <ManageCoaches />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/staff",
        element: (
          <ProtectedRoute requiredRole={["schoolstaff", "districtadmin"]}>
            <ManageStaff />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/admins",
        element: (
          <ProtectedRoute requiredRole={["districtadmin"]}>
            <ManageAdmins />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/climbers",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <ClimberSearch />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/climber/:climberId",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <ClimberDetail />
          </ProtectedRoute>
        ),
      },
      // Sites
      {
        path: "sites",
        element: (
          <ProtectedRoute requiredRole={["schoolstaff", "districtadmin"]}>
            <Sites />
          </ProtectedRoute>
        ),
      },
      {
        path: "sites/school/:schoolID",
        element: (
          <ProtectedRoute requiredRole={["schoolstaff", "districtadmin"]}>
            <SchoolGroups />
          </ProtectedRoute>
        ),
      },
      // Educator Downloads
      {
        path: "educator-downloads",
        element: <EducatorDownloads />,
      },
      {
        path: "educator-downloads/lessons",
        element: <EducatorDownloads resourceToDisplay="Lessons" />,
      },
      {
        path: "educator-downloads/implementation-materials",
        element: (
          <EducatorDownloads resourceToDisplay="Implementation Materials" />
        ),
      },
      // Climber Experience
      {
        path: "climber-experience/courses",
        element: <Outlet />,
        children: [
          { element: <Courses />, index: true },
          { path: "career-climbs/:id", element: <ClimberCareerClimbItem /> },
        ],
      },
      {
        path: "climber-experience/career-climbs",
        element: <CoachCareerClimbs />,
      },
      {
        path: "climber-experience/career-climbs/:id",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <CoachCareerClimbItem />
          </ProtectedRoute>
        ),
      },

      {
        path: "climber-experience/courses/:id",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <CoachCareerClimbItem />
          </ProtectedRoute>
        ),
      },

      // Ladders (HS only)
      {
        path: "climber-experience/ladders",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
          >
            <Suspense fallback={<HatLoading />}>
              <Outlet />
            </Suspense>
          </ProtectedRoute>
        ),
        children: [{ element: <BrowseLadders />, index: true }],
      },
      // Hats (non-MS)
      {
        path: "climber-experience/hats",
        element: (
          <ProtectedRoute requiredLevel={["hs", "wf"]}>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [{ element: <HatsMain />, index: true }],
      },

      // Settings
      {
        path: "settings",
        element: (
          <ProtectedRoute requiredRole={["districtadmin"]}>
            <Settings />
          </ProtectedRoute>
        ),
      },
      // Course Plan Configuration
      {
        path: "settings/courses",
        element: (
          <ProtectedRoute requiredRole={["districtadmin"]}>
            <CoursePlanConfig />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    // -------- Climbers for Teachers (HS only) ----------
    element: (
      <ProtectedRoute requiredRole={["teacher"]} requiredLevel={["hs"]}>
        <MainLayout>
          <Suspense fallback={<HatLoading />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "climbers",
        element: <Outlet />,
        children: [
          { element: <ClimberList />, index: true },
          { path: ":climberId", element: <RedirectToClimberDetail /> },
        ],
      },
    ],
  },
  {
    // -------- Dashboards ----------
    element: (
      <MainLayout>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </MainLayout>
    ),
    children: [
      // Climber Dashboard
      {
        path: "climber/dashboard",
        element: (
          <ProtectedRoute
            requiredRole={["climber"]}
            requiredLevel={["hs", "wf"]}
          >
            <ClimberDashboard />
          </ProtectedRoute>
        ),
      },
      // Teacher Dashboard & Features
      {
        path: "teacher/dashboard",
        element: (
          <ProtectedRoute requiredRole={["teacher"]}>
            <TeacherDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: "teacher/review-answers/:sessionId/:userId",
        element: (
          <ProtectedRoute
            requiredRole={[
              "teacher",
              "schoolstaff",
              "districtadmin",
              "superadmin",
            ]}
            requiredLevel={["hs", "ms"]}
          >
            <StudentAnswerReview />
          </ProtectedRoute>
        ),
      },
      // District Admin Dashboard
      // For Dadmins:
      {
        path: "districtadmin/dashboard",
        element: (
          <ProtectedRoute requiredRole={["districtadmin"]}>
            <DistrictAdminDashboard />
          </ProtectedRoute>
        ),
      },

      // For Sadmins:
      {
        path: "districtadmin/dashboard/:districtId",
        element: (
          <ProtectedRoute requiredRole={["superadmin"]}>
            <DistrictAdminDashboard />
          </ProtectedRoute>
        ),
      },
      // School Staff Dashboard
      {
        path: "schoolstaff/dashboard",
        element: (
          <ProtectedRoute requiredRole={["schoolstaff"]}>
            <SchoolStaffDashboard />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // -------- Super Admin Dashboard (no sidebar) ----------
  {
    element: (
      <ProtectedRoute requiredRole={["superadmin"]}>
        <Suspense fallback={<HatLoading />}>
          <Outlet />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "superadmin/dashboard",
        element: <SuperAdminDashboard />,
      },
    ],
  },

  {
    // -------- login ----------
    element: (
      <Suspense fallback={<HatLoading />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: "",
        children: [{ element: <Login />, index: true }],
      },
      {
        path: "post-login",
        children: [{ element: <PostLogin />, index: true }],
      },
    ],
  },
  {
    element: (
      <MainLayout>
        <Outlet />
      </MainLayout>
    ),
    children: [{ path: "404", element: <Page404 /> }],
  },
];
